import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, X, Copy, Check, ScrollText, Trash2, Power, ShoppingBag, Store, Link2, RefreshCw, Zap, DownloadCloud, Loader2, KeyRound,
} from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

type Channel = {
  id: number;
  channelType: 'SHOPIFY' | 'WOOCOMMERCE' | 'API';
  name?: string | null;
  storeName?: string | null;
  storeUrl?: string | null;
  shopDomain?: string | null;
  webhookUrl?: string | null;
  isActive: boolean;
  lastSyncAt?: string | null;
  defaultWarehouseId?: number | null;
  prepaidCourierId?: number | null;
  codCourierId?: number | null;
  stripCountryCode?: boolean;
  createdAt: string;
  /** Presence flags only — the secrets themselves never leave the server. */
  hasAccessToken?: boolean;
  hasWebhookSecret?: boolean;
  hasWebhookSigningSecret?: boolean;
};
type WebhookLogRow = { id: string; topic: string; status: string; note: string; at: string; payload: any };

const TYPE_BADGE: Record<string, string> = {
  SHOPIFY: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  WOOCOMMERCE: 'bg-violet-50 text-violet-700 border-violet-200',
  API: 'bg-slate-50 text-slate-700 border-slate-200',
};
const STATUS_BADGE: Record<string, string> = {
  processed: 'bg-emerald-50 text-emerald-700',
  received: 'bg-blue-50 text-blue-700',
  ignored: 'bg-slate-100 text-slate-600',
  failed: 'bg-rose-50 text-rose-700',
  // A delivery that reached us and was refused on signature — distinct from "failed" (accepted, then
  // broke while being turned into an order), because the two need completely different fixes.
  rejected: 'bg-amber-100 text-amber-800',
};

export function ChannelsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isMerchant = user?.kind === 'merchant';

  // Admin picks a merchant; merchant portal is always "me".
  const [pickedMerchant, setPickedMerchant] = useState<number | ''>('');
  const merchantPath = isMerchant ? 'me' : (pickedMerchant || '');
  const scopeReady = isMerchant || !!pickedMerchant;

  const [addOpen, setAddOpen] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const [logsFor, setLogsFor] = useState<Channel | null>(null);
  const [credsFor, setCredsFor] = useState<Channel | null>(null);

  // Show the outcome of the OAuth round-trip (Shopify → backend callback → back here with ?shopify=…).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const r = params.get('shopify');
    if (!r) return;
    if (r === 'connected') toast.success(`Shopify store connected${params.get('shop') ? ` · ${params.get('shop')}` : ''}. Past orders are importing in the background.`);
    else if (r === 'error') toast.error(`Shopify connection failed: ${params.get('reason') || 'unknown error'}`);
    // Clean the query string so a refresh doesn't re-toast.
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const { data: merchants } = useQuery({
    queryKey: ['merchants-light-channels'],
    queryFn: async () => (await api.get<any>('/merchants', { params: { pageSize: 500 } })).data,
    enabled: !isMerchant,
  });
  const merchantList = useMemo(() => (Array.isArray(merchants) ? merchants : (merchants as any)?.items ?? []), [merchants]);

  const { data: channels, isLoading, isError, error, refetch } = useQuery<Channel[]>({
    queryKey: ['channels', merchantPath],
    queryFn: async () => (await api.get<Channel[]>(`/merchants/${merchantPath}/channels`)).data,
    enabled: scopeReady,
  });
  // The API "channel" carries no store and no webhook — it exists so orders the merchant POSTs to our
  // API (the Orders Bucket) have a default pickup location, set with the picker on its row.
  const addApi = useMutation({
    mutationFn: async () => (await api.post(`/merchants/${merchantPath}/channels`, { channelType: 'API', name: 'API orders', auth: {} })).data,
    onSuccess: () => { toast.success('API orders channel added — now choose its default pickup location'); qc.invalidateQueries({ queryKey: ['channels'] }); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not add the API orders channel'),
  });


  const toggleMut = useMutation({
    mutationFn: async (c: Channel) => (await api.patch(`/merchants/${merchantPath}/channels/${c.id}/toggle`, { isActive: !c.isActive })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['channels', merchantPath] }); },
    onError: () => toast.error('Could not update channel'),
  });
  const deleteMut = useMutation({
    mutationFn: async (c: Channel) => (await api.delete(`/merchants/${merchantPath}/channels/${c.id}`)).data,
    onSuccess: () => { toast.success('Channel removed'); qc.invalidateQueries({ queryKey: ['channels', merchantPath] }); },
    onError: () => toast.error('Could not remove channel'),
  });
  const syncMut = useMutation({
    // A first sync pages through the whole order history one Shopify call at a time, which routinely
    // runs past the client's 30s default — at which point the browser discards a response the server
    // is still going to produce, and the merchant is told it failed while the import is running fine.
    mutationFn: async (c: Channel) => (await api.post(`/shopify/channels/${c.id}/sync`, {}, { timeout: 600_000 })).data,
    onSuccess: (r: any) => {
      toast.success(`Imported past orders: ${r?.created ?? 0} new, ${r?.updated ?? 0} updated${r?.truncated ? ' (more remain — run again)' : ''}.`);
      qc.invalidateQueries({ queryKey: ['channels', merchantPath] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Sync failed — is this store connected via OAuth?', { duration: 9000 }),
  });

  return (
    <div className="p-6 max-w-[1100px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ShoppingBag size={22} className="text-emerald-700" /> Sales Channels</h1>
          <p className="text-slate-500 text-sm mt-0.5">Connect your Shopify store — orders flow into <b>Not Shipped</b> automatically.</p>
        </div>
        {scopeReady && (
          <div className="flex items-center gap-2">
            <button onClick={() => setConnectOpen(true)} className="px-3 py-2 bg-emerald-600 text-white rounded text-sm inline-flex items-center gap-1 hover:bg-emerald-700">
              <Zap size={14} /> Connect Shopify
            </button>
            {!channels?.some((c) => c.channelType === 'API') && (
              <button onClick={() => addApi.mutate()} disabled={addApi.isPending} data-testid="add-api-channel" title="Set the pickup location for orders this merchant sends to our API (Orders Bucket)" className="px-3 py-2 border border-slate-300 text-slate-700 rounded text-sm inline-flex items-center gap-1 hover:bg-slate-50 disabled:opacity-50">
                <Plus size={14} /> API orders
              </button>
            )}
            <button onClick={() => setAddOpen(true)} title="Manual setup with a paste-in access token (legacy)" className="px-3 py-2 border border-slate-300 text-slate-600 rounded text-sm inline-flex items-center gap-1 hover:bg-slate-50">
              <Plus size={14} /> Manual token
            </button>
          </div>
        )}
      </div>

      {!isMerchant && (
        <div className="mb-4 max-w-md">
          <label className="text-xs text-slate-500">Merchant</label>
          <SearchableSelect
            value={pickedMerchant}
            onChange={(v) => setPickedMerchant(v === '' ? '' : Number(v))}
            options={merchantList.map((m: any) => ({ value: m.id, label: `#${m.id} · ${m.companyName}`, hint: m.gstin ?? undefined }))}
            placeholder="Select a merchant to manage their channels…"
          />
        </div>
      )}

      {!scopeReady ? (
        <div className="text-center py-16 text-slate-400">Select a merchant to manage their sales channels.</div>
      ) : isLoading ? (
        <div className="text-center py-16 text-slate-400">Loading channels…</div>
      ) : isError ? (
        // A failed request used to fall through to the empty state below, so "we couldn't ask" and
        // "there are none" looked identical — which sends people hunting for a missing channel that
        // is actually sitting there. Say which it is.
        <div className="border border-rose-200 bg-rose-50 rounded-xl p-6 text-center">
          <div className="text-rose-800 font-medium">Couldn't load your channels</div>
          <p className="text-sm text-rose-700 mt-1">
            {(error as any)?.response?.status === 401 || (error as any)?.response?.status === 403
              ? 'Your session may have expired — sign out and back in.'
              : (error as any)?.response?.data?.message ?? (error as any)?.message ?? 'The request failed.'}
          </p>
          <p className="text-[11px] text-rose-600 mt-1">
            This is a load failure, not an empty list — any channels you have are still there.
          </p>
          <button onClick={() => refetch()} className="mt-3 px-3 py-1.5 border border-rose-300 text-rose-700 rounded text-sm inline-flex items-center gap-1 hover:bg-rose-100">
            <RefreshCw size={13} /> Try again
          </button>
        </div>
      ) : (channels?.length ?? 0) === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-xl p-10 text-center">
          <Store size={30} className="mx-auto text-slate-300 mb-2" />
          <div className="text-slate-600 font-medium">No channels yet</div>
          <p className="text-sm text-slate-400 mt-1">Add a Shopify channel to start pulling orders into the panel.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {channels!.map((c) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`border rounded-full px-2 py-0.5 text-xs ${TYPE_BADGE[c.channelType] ?? ''}`}>{c.channelType}</span>
                    <span className="font-semibold text-slate-900">{c.name || c.storeName || `Channel #${c.id}`}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{c.isActive ? 'Active' : 'Paused'}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {c.storeUrl && <a href={c.storeUrl} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">{c.storeUrl}</a>}
                    {c.lastSyncAt && <span className="ml-2">· last order {new Date(c.lastSyncAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>}
                  </div>
                  {c.channelType === 'SHOPIFY' && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded border ${c.hasAccessToken ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
                        {c.hasAccessToken ? 'Access token saved' : 'No access token — cannot sync orders'}
                      </span>
                      <span className={`text-[11px] px-1.5 py-0.5 rounded border ${(c.hasWebhookSecret || c.hasWebhookSigningSecret) ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                        {[c.hasWebhookSecret && 'app secret', c.hasWebhookSigningSecret && 'signing secret'].filter(Boolean).join(' + ') || 'Webhooks verified with our app secret'}
                        {(c.hasWebhookSecret || c.hasWebhookSigningSecret) ? ' saved' : ''}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {c.channelType === 'SHOPIFY' && (
                    <button onClick={() => syncMut.mutate(c)} disabled={syncMut.isPending} title="Pull historical orders from Shopify into Not Shipped" className="text-xs px-2 py-1 border border-emerald-200 text-emerald-700 rounded hover:bg-emerald-50 inline-flex items-center gap-1 disabled:opacity-50">
                      <DownloadCloud size={12} className={syncMut.isPending ? 'animate-pulse' : ''} /> Sync past orders
                    </button>
                  )}
                  {c.channelType === 'SHOPIFY' && (
                    <button onClick={() => setCredsFor(c)} title="Set the Admin API access token and the app secret webhooks are signed with" className="text-xs px-2 py-1 border border-slate-200 text-slate-600 rounded hover:bg-slate-50 inline-flex items-center gap-1"><KeyRound size={12} /> Credentials</button>
                  )}
                  <button onClick={() => setLogsFor(c)} className="text-xs px-2 py-1 border border-slate-200 text-slate-600 rounded hover:bg-slate-50 inline-flex items-center gap-1"><ScrollText size={12} /> Logs</button>
                  <button onClick={() => toggleMut.mutate(c)} className="text-xs px-2 py-1 border border-slate-200 text-slate-600 rounded hover:bg-slate-50 inline-flex items-center gap-1"><Power size={12} /> {c.isActive ? 'Pause' : 'Resume'}</button>
                  <button onClick={() => { if (confirm(`Remove channel "${c.name || c.storeName}"? Its webhook URL will stop working.`)) deleteMut.mutate(c); }} className="text-xs px-2 py-1 border border-rose-200 text-rose-600 rounded hover:bg-rose-50 inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
                </div>
              </div>
              {c.channelType === 'API' && (
                <p className="mt-2 text-xs text-slate-500" data-testid="api-channel-note">
                  Orders this merchant sends to our API land in the <b>Orders Bucket</b>. When they are moved to B2C / B2B, they get the pickup location below.
                </p>
              )}
              <DefaultWarehousePicker channel={c} merchantPath={merchantPath} onSaved={() => qc.invalidateQueries({ queryKey: ['channels'] })} />
              {c.channelType !== 'API' && <DefaultCourierPickers channel={c} merchantPath={merchantPath} onSaved={() => qc.invalidateQueries({ queryKey: ['channels'] })} />}
              {c.channelType !== 'API' && <PhoneRuleToggle channel={c} merchantPath={merchantPath} onSaved={() => qc.invalidateQueries({ queryKey: ['channels'] })} />}

              {c.webhookUrl && c.channelType !== 'API' && (
                <div className="mt-3">
                  <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Webhook URL (paste into Shopify)</div>
                  <CopyRow value={c.webhookUrl} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {connectOpen && scopeReady && (
        <ConnectShopifyModal
          merchantId={isMerchant ? undefined : Number(pickedMerchant)}
          onClose={() => setConnectOpen(false)}
        />
      )}
      {addOpen && scopeReady && (
        <AddShopifyModal
          merchantPath={String(merchantPath)}
          onClose={() => setAddOpen(false)}
          onDone={() => qc.invalidateQueries({ queryKey: ['channels', merchantPath] })}
        />
      )}
      {logsFor && (
        <LogsModal merchantPath={String(merchantPath)} channel={logsFor} onClose={() => setLogsFor(null)} />
      )}
      {credsFor && (
        <CredentialsModal
          merchantPath={String(merchantPath)}
          channel={credsFor}
          onClose={() => setCredsFor(null)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['channels', merchantPath] })}
        />
      )}
    </div>
  );
}

function CopyRow({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error('Copy failed — select and copy manually'); }
  };
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 bg-slate-50 border border-slate-200 rounded px-2 py-1.5 text-xs font-mono text-slate-700 break-all">{value}</code>
      <button onClick={copy} className="px-2 py-1.5 border border-slate-200 rounded text-xs inline-flex items-center gap-1 hover:bg-slate-50 shrink-0">
        {copied ? <><Check size={12} className="text-emerald-600" /> Copied</> : <><Copy size={12} /> Copy</>}
      </button>
    </div>
  );
}

/**
 * Set the two Shopify secrets on an existing channel. They are deliberately presented apart, with what
 * each one does spelled out: the access token is what WE call Shopify with, the app secret is what
 * SHOPIFY signs webhooks with. Pasting one into the other's box is the mistake this screen exists to
 * stop, and the server refuses it either way round.
 */
/** One revealed credential — shown in full (never masked) with a copy button. */
function RevealRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="text-[11px] text-slate-500">{label}</div>
      {value
        ? <div className="flex items-center gap-1.5">
            <code className="flex-1 min-w-0 break-all bg-white border border-slate-200 rounded px-2 py-1 text-[11px] font-mono">{value}</code>
            <button onClick={() => { navigator.clipboard?.writeText(value); toast.success(`${label} copied`); }}
              title="Copy" className="text-[11px] px-2 py-1 border border-slate-300 rounded hover:bg-white shrink-0">Copy</button>
          </div>
        : <div className="text-[11px] text-slate-400 italic">not set</div>}
    </div>
  );
}

function CredentialsModal({ merchantPath, channel, onClose, onSaved }: { merchantPath: string; channel: Channel; onClose: () => void; onSaved: () => void }) {
  const [accessToken, setAccessToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [signingSecret, setSigningSecret] = useState('');
  const [check, setCheck] = useState<{ ok: boolean; message: string } | null>(null);

  const save = useMutation({
    mutationFn: async () =>
      (await api.patch(`/merchants/${merchantPath}/channels/${channel.id}/credentials`, {
        ...(accessToken.trim() ? { accessToken: accessToken.trim() } : {}),
        ...(webhookSecret.trim() ? { webhookSecret: webhookSecret.trim() } : {}),
        ...(signingSecret.trim() ? { webhookSigningSecret: signingSecret.trim() } : {}),
      })).data,
    onSuccess: (r: any) => {
      toast.success(`Saved${r?.shopDomain ? ` · store confirmed as ${r.shopDomain}` : ''}.`);
      setAccessToken(''); setWebhookSecret(''); setSigningSecret('');
      onSaved();
      checkMut.mutate();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save credentials', { duration: 9000 }),
  });

  // Straight answer on whether what's saved actually verifies, tested against the last delivery we
  // refused — otherwise the only way to find out is to go back to Shopify and send another test.
  const checkMut = useMutation({
    mutationFn: async () => (await api.post(`/shopify/channels/${channel.id}/check-secret`, {})).data,
    onSuccess: (r: any) => setCheck({ ok: !!r?.ok, message: r?.message ?? '' }),
    onError: (e: any) => setCheck({ ok: false, message: e?.response?.data?.message ?? 'Check failed' }),
  });

  const canSave = !!accessToken.trim() || !!webhookSecret.trim() || !!signingSecret.trim();

  // Show what is actually stored. These are encrypted-but-reversible (we have to call Shopify with
  // them), so unlike a password they can genuinely be shown — and "is the value you hold the same
  // one I pasted?" is the question behind most failed Shopify setups. Fetched only when asked, so
  // the secrets aren't sitting in the channel-list cache on every page view.
  const [revealed, setRevealed] = useState<any | null>(null);
  const reveal = useMutation({
    mutationFn: async () => (await api.get(`/merchants/${merchantPath}/channels/${channel.id}/credentials/reveal`)).data,
    onSuccess: (r: any) => setRevealed(r),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not read the stored credentials'),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-[580px] max-w-full">
        <div className="bg-slate-800 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><KeyRound size={16} /> Credentials · {channel.name || channel.storeName}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          {/* ── What is stored right now ── */}
          <div className="rounded border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-medium text-slate-700">Stored values</div>
              {revealed
                ? <button onClick={() => setRevealed(null)} className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-white">Hide</button>
                : <button onClick={() => reveal.mutate()} disabled={reveal.isPending}
                    className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-white disabled:opacity-50">
                    {reveal.isPending ? 'Loading…' : 'Show token & secrets'}
                  </button>}
            </div>
            {revealed && (
              <div className="mt-2 space-y-2">
                <RevealRow label="Admin API access token" value={revealed.accessToken} />
                <RevealRow label="App API secret key" value={revealed.webhookSecret} />
                <RevealRow label="Webhook signing secret" value={revealed.webhookSigningSecret} />
                <RevealRow label="Store domain" value={revealed.shopDomain} />
                <RevealRow label="Webhook URL" value={revealed.webhookUrl} />
                {Object.entries(revealed.other ?? {}).map(([k, v]) => (
                  <RevealRow key={k} label={k} value={typeof v === 'string' ? v : JSON.stringify(v)} />
                ))}
                <p className="text-[11px] text-amber-700">
                  Anyone who can read these can act on the store. Don't paste them into a chat or a screenshot.
                </p>
              </div>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Admin API access token {channel.hasAccessToken && <span className="text-emerald-600">· saved</span>}</label>
            <input value={accessToken} onChange={(e) => setAccessToken(e.target.value)} placeholder="shpat_…" className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
            <p className="text-[11px] text-gray-400 mt-1">
              What <b>we</b> use to pull orders and push tracking back. Shopify → your app → <b>API credentials</b> → <b>Install app</b> → <i>Admin API access token</i>. We verify it against your store before saving.
            </p>
            {webhookSecret.trim().startsWith('shpat_') && <p className="text-[11px] text-red-600 mt-1">This value belongs in the box above, not below.</p>}
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">App API secret key {channel.hasWebhookSecret && <span className="text-emerald-600">· saved</span>}</label>
            <input value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)} placeholder="shpss_…  or  client secret" className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
            <p className="text-[11px] text-gray-400 mt-1">
              What <b>Shopify</b> signs webhooks with. Only needed if the webhooks are created by <b>your own custom app</b> — set this and we'll verify its deliveries against it instead of our app secret. Leave blank for stores connected with <b>Connect Shopify</b>.
            </p>
            {accessToken.trim().startsWith('shpss_') && <p className="text-[11px] text-red-600 mt-1">That looks like the app secret — it belongs in the box below.</p>}
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Webhook signing secret {channel.hasWebhookSigningSecret && <span className="text-emerald-600">· saved</span>}</label>
            <input value={signingSecret} onChange={(e) => setSigningSecret(e.target.value)} placeholder="64-character value from Settings → Notifications" className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
            <p className="text-[11px] text-gray-400 mt-1">
              For webhooks you created <b>by hand</b> in Shopify → <b>Settings → Notifications → Webhooks</b>. Shopify signs those with the <b>store's own</b> secret, shown on that same page — not with any app's secret. Save it here and we'll verify those deliveries against it.
            </p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-[11px] text-slate-600">
            We try <b>every</b> secret saved here against each delivery, so it's safe to fill in more than one if webhooks come from more than one source.
          </div>
          {check && (
            <div className={`rounded border p-3 text-xs ${check.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              <b>{check.ok ? 'Secret verified' : 'Not verified'}</b> — {check.message}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-between gap-3">
          <button
            onClick={() => checkMut.mutate()}
            disabled={checkMut.isPending}
            title="Test what's saved against the last delivery we refused"
            className="border border-slate-300 text-slate-600 px-3 py-1.5 rounded text-sm disabled:opacity-50"
          >
            {checkMut.isPending ? 'Checking…' : 'Test saved secret'}
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-1.5 rounded text-sm">Close</button>
            <button onClick={() => save.mutate()} disabled={!canSave || save.isPending} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConnectShopifyModal({ merchantId, onClose }: { merchantId?: number; onClose: () => void }) {
  const [store, setStore] = useState('');
  const [loading, setLoading] = useState(false);

  const connect = async () => {
    if (!store.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get<{ installUrl: string }>('/shopify/install', {
        params: { shop: store.trim(), ...(merchantId ? { merchantId } : {}) },
      });
      if (data?.installUrl) window.location.href = data.installUrl; // → Shopify consent screen
      else { toast.error('Could not start the Shopify connection'); setLoading(false); }
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not start the Shopify connection');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-[520px] max-w-full">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><Zap size={16} /> Connect Shopify</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Your Shopify store *</label>
            <input
              value={store}
              onChange={(e) => setStore(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') connect(); }}
              placeholder="e.g. mystore  or  mystore.myshopify.com"
              autoFocus
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
            <p className="text-[11px] text-gray-400 mt-1">You'll be sent to Shopify to approve access. On approval we auto-register webhooks and import your past orders — no token to copy.</p>
          </div>
          <details className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <summary className="cursor-pointer font-medium text-slate-700">Where do I find my store name?</summary>
            <ul className="list-disc pl-4 mt-2 space-y-1">
              <li>In Shopify go to <b>Settings → Domains</b> and copy the domain ending in <b>.myshopify.com</b> — e.g. <code className="bg-white border rounded px-1">rxezer-5c.myshopify.com</code>. That is the only value that works here.</li>
              <li className="text-amber-700"><b>Don't use the address bar of your admin.</b> <code className="bg-white border rounded px-1">admin.shopify.com/store/<b>my-shop</b></code> shows a handle made from your store <i>name</i>, and on most stores that is <b>not</b> your domain — <code className="bg-white border rounded px-1">my-shop.myshopify.com</code> is often a different shop entirely.</li>
              <li>Your own custom domain (<code className="bg-white border rounded px-1">www.yourbrand.com</code>) will not work — it must be the <b>.myshopify.com</b> one.</li>
            </ul>
          </details>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            After you click <b>Continue</b>: Shopify shows a permission screen → <b>Install</b> → you're brought back here and orders start flowing into <b>Not Shipped</b>.
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-3">
          <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-1.5 rounded text-sm">Cancel</button>
          <button onClick={connect} disabled={!store.trim() || loading} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50 inline-flex items-center gap-1">
            {loading ? 'Redirecting…' : <>Continue <Zap size={13} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddShopifyModal({ merchantPath, onClose, onDone }: { merchantPath: string; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [storeName, setStoreName] = useState('');
  const [token, setToken] = useState('');
  const [secret, setSecret] = useState('');
  const [created, setCreated] = useState<Channel | null>(null);

  const save = useMutation({
    mutationFn: async () =>
      (await api.post<Channel>(`/merchants/${merchantPath}/channels`, {
        channelType: 'SHOPIFY',
        name: name.trim(),
        storeName: storeName.trim(),
        auth: { accessToken: token.trim(), ...(secret.trim() ? { webhookSecret: secret.trim() } : {}) },
      })).data,
    onSuccess: (c) => { setCreated(c); onDone(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not create channel'),
  });

  // Token is optional: a channel can legitimately exist to RECEIVE webhooks before anyone has
  // minted an Admin API token. Without it the channel just can't pull/push — the row already says
  // "No access token — cannot sync orders", and the token can be added later under Credentials.
  const canSave = !!name.trim() && !!storeName.trim();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-[560px] max-w-full">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><ShoppingBag size={16} /> {created ? 'Channel Created' : 'Add Shopify Channel'}</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>

        {!created ? (
          <>
            <div className="p-5 space-y-4 text-sm">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Channel Name *</label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My Shopify Store" className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Shopify Store Name *</label>
                <input value={storeName} onChange={(e) => setStoreName(e.target.value)} placeholder="e.g. rxezer-5c.myshopify.com" className="w-full border rounded px-2 py-1.5 text-sm" />
                <p className="text-[11px] text-gray-400 mt-1">
                  Copy this from Shopify → <b>Settings → Domains</b> (the <b>.myshopify.com</b> one). Not the address bar of your admin — <code className="bg-slate-50 border rounded px-1">admin.shopify.com/store/x</code> usually does <b>not</b> mean your domain is <code className="bg-slate-50 border rounded px-1">x.myshopify.com</code>.
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Shopify App / Admin API Access Token <span className="text-gray-400">(optional)</span></label>
                <input value={token} onChange={(e) => setToken(e.target.value)} placeholder="shpat_…" className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
                <p className="text-[11px] text-gray-400 mt-1">Stored encrypted — used to pull orders and push tracking back to Shopify. If you enter one we check it against your store before saving.</p>
                {!token.trim() && (
                  <p className="text-[11px] text-amber-700 mt-1">
                    Leaving this blank is fine — the channel will receive webhooks but <b>cannot sync past orders or push tracking</b> until a token is added under <b>Credentials</b>.
                  </p>
                )}
                {token.trim().startsWith('shpss_') && (
                  <p className="text-[11px] text-red-600 mt-1">
                    That's the <b>API secret key</b>, not the access token. On the same API credentials page click <b>Install app</b>, then copy the <b>Admin API access token</b> — it starts with <code className="bg-white border rounded px-1">shpat_</code>.
                  </p>
                )}
                <details className="mt-2 rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  <summary className="cursor-pointer font-medium text-slate-700">How do I get this access token?</summary>
                  <p className="mt-2 text-[11px] text-amber-700">Tip: most merchants should use the <b>Connect Shopify</b> button instead — it needs no token. Use this only if you prefer a manual custom app.</p>
                  <ol className="list-decimal pl-4 mt-2 space-y-1">
                    <li>Shopify admin → <b>Settings → Apps and sales channels → Develop apps</b>.</li>
                    <li>Click <b>Create an app</b> → give it a name (e.g. “Gaint Logistics”) → <b>Create app</b>.</li>
                    <li>Open <b>Configuration → Admin API integration → Configure</b> and tick: <code className="bg-white border rounded px-1">read_orders</code>, <code className="bg-white border rounded px-1">write_orders</code>, <code className="bg-white border rounded px-1">read_fulfillments</code>, <code className="bg-white border rounded px-1">write_fulfillments</code>, <code className="bg-white border rounded px-1">read_merchant_managed_fulfillment_orders</code>, <code className="bg-white border rounded px-1">write_merchant_managed_fulfillment_orders</code> → <b>Save</b>.</li>
                    <li>Go to <b>API credentials → Install app</b>.</li>
                    <li>Under <b>Admin API access token</b> click <b>Reveal token once</b> — it starts with <code className="bg-white border rounded px-1">shpat_</code>. Copy it here (you can only see it once).</li>
                  </ol>
                </details>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">App API secret key <span className="text-gray-400">(optional)</span></label>
                <input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="shpss_…  or  client secret" className="w-full border rounded px-2 py-1.5 text-sm font-mono" />
                <p className="text-[11px] text-gray-400 mt-1">
                  Only if <b>your own custom app</b> will create the webhooks. Shopify signs each webhook with the secret of the app that created it, so without this we can't verify those deliveries and they're refused. It's on the same <b>API credentials</b> page, shown as <i>API secret key</i> / <i>Client secret</i>.
                </p>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-3">
              <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-1.5 rounded text-sm">Cancel</button>
              <button onClick={() => save.mutate()} disabled={!canSave || save.isPending} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm disabled:opacity-50">
                {save.isPending ? 'Creating…' : 'Create Channel'}
              </button>
            </div>
          </>
        ) : (
          <div className="p-5 space-y-4 text-sm">
            <div className="rounded border border-emerald-200 bg-emerald-50 text-emerald-800 px-3 py-2 flex items-center gap-2">
              <Check size={16} /> Channel <b>{created.name}</b> is ready. Now connect it in Shopify.
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1"><Link2 size={12} /> Your Webhook URL</div>
              <CopyRow value={created.webhookUrl ?? ''} />
            </div>
            <div className="rounded border border-amber-300 bg-amber-50 p-3">
              <div className="font-medium text-amber-900 mb-1.5">Live orders need “Connect Shopify”, not this channel</div>
              <p className="text-xs text-amber-800">
                A webhook you create yourself under <b>Settings → Notifications</b> is signed by Shopify with <b>your store's</b> secret, which we have no way to check — we reject those payloads as unverified, so no orders arrive. Signature checking is a Shopify requirement we can't switch off.
              </p>
              <p className="text-xs text-amber-800 mt-2">
                This channel can still <b>import past orders</b> with the token you just saved (<b>Sync past orders</b>). For orders to flow in automatically, go back and use <b>Connect Shopify</b> — it registers the webhooks itself and needs no token.
              </p>
            </div>
            <div className="rounded border border-slate-200 bg-slate-50 p-3">
              <p className="text-[11px] text-slate-500">Imported orders appear under <b>Orders → Not Shipped</b> (channel “{created.name}”). Every payload we do receive is saved in this channel's <b>Logs</b>.</p>
            </div>
            <div className="flex justify-end">
              <button onClick={onClose} className="bg-emerald-600 text-white px-5 py-1.5 rounded text-sm">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const ORDER_TOPICS = ['orders/create', 'orders/updated', 'orders/paid', 'orders/edited'];

function LogsModal({ merchantPath, channel, onClose }: { merchantPath: string; channel: Channel; onClose: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const { data, isLoading, refetch, isFetching } = useQuery<WebhookLogRow[]>({
    queryKey: ['channel-logs', merchantPath, channel.id],
    queryFn: async () => (await api.get<WebhookLogRow[]>(`/merchants/${merchantPath}/channels/${channel.id}/logs`)).data,
  });

  // Rescue an order from a delivery that was refused (usually a signature mismatch while the store's
  // secret is still being sorted out). The payload was captured, so nothing is actually lost — this
  // turns it into an order on an authenticated click rather than by trusting the unsigned POST.
  const saveMut = useMutation({
    mutationFn: async (r: WebhookLogRow) =>
      (await api.post(`/shopify/channels/${channel.id}/logs/${r.id}/reprocess`, {})).data,
    onSuccess: (d: any) => { toast.success(d?.note ?? 'Order saved'); refetch(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save this order', { duration: 9000 }),
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center pt-12 z-50 p-4">
      <div className="bg-white rounded-lg w-[720px] max-w-full max-h-[85vh] flex flex-col">
        <div className="bg-emerald-700 text-white px-5 py-3 flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2"><ScrollText size={16} /> Webhook Logs · {channel.name || channel.storeName}</div>
          <div className="flex items-center gap-2">
            <button onClick={() => refetch()} title="Refresh" className="opacity-90 hover:opacity-100"><RefreshCw size={15} className={isFetching ? 'animate-spin' : ''} /></button>
            <button onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        <div className="p-4 overflow-y-auto flex-1 text-sm">
          {isLoading ? (
            <div className="text-center py-10 text-slate-400">Loading…</div>
          ) : (data?.length ?? 0) === 0 ? (
            <div className="text-center py-10 text-slate-400">No webhooks received yet. Send a test order from Shopify to see it here.</div>
          ) : (
            <ul className="space-y-2">
              {data!.map((r) => (
                <li key={r.id} className="border border-slate-200 rounded">
                  <button onClick={() => setOpen(open === r.id ? null : r.id)} className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${STATUS_BADGE[r.status] ?? 'bg-slate-100 text-slate-600'}`}>{r.status}</span>
                      <span className="font-mono text-xs text-slate-600 shrink-0">{r.topic || '—'}</span>
                      <span className="text-xs text-slate-500 truncate">{r.note}</span>
                    </div>
                    <span className="text-[11px] text-slate-400 shrink-0">{new Date(r.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                  </button>
                  {r.status === 'rejected' && ORDER_TOPICS.includes(r.topic) && (
                    <div className="px-3 py-2 border-t border-slate-100 bg-amber-50 flex flex-wrap items-center justify-between gap-2">
                      <span className="text-[11px] text-amber-800">
                        Refused on signature — the order was <b>not</b> imported. The payload is kept, so you can still save it.
                      </span>
                      <button
                        onClick={() => saveMut.mutate(r)}
                        disabled={saveMut.isPending}
                        className="text-xs px-2 py-1 border border-amber-300 text-amber-900 rounded bg-white hover:bg-amber-100 disabled:opacity-50 shrink-0"
                      >
                        {saveMut.isPending ? 'Saving…' : 'Save this order anyway'}
                      </button>
                    </div>
                  )}
                  {open === r.id && (
                    <pre className="bg-slate-900 text-slate-100 text-[11px] rounded-b p-3 overflow-x-auto max-h-72 overflow-y-auto">{JSON.stringify(r.payload, null, 2)}</pre>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Pickup location stamped on every order this channel brings in.
 *
 * Channel orders (Shopify webhooks, backfill, API) carry no pickup address of their own, so without
 * this they land with none and have to be set one at a time. Lists the merchant's own warehouses plus
 * the shared Gaint master warehouses — the same set the Create-Order pickup dropdown offers.
 */
function DefaultWarehousePicker({ channel, merchantPath, onSaved }: { channel: Channel; merchantPath: string | number; onSaved: () => void }) {
  const { data: warehouses = [], isLoading } = useQuery({
    queryKey: ['channel-warehouses', merchantPath],
    queryFn: async () => (await api.get<any[]>(`/merchants/${merchantPath}/warehouses`, { params: { includeMaster: 'true' } })).data,
  });

  const save = useMutation({
    mutationFn: async (warehouseId: number | null) =>
      (await api.patch(`/merchants/${merchantPath}/channels/${channel.id}/default-warehouse`, { warehouseId })).data,
    onSuccess: () => { toast.success('Default pickup location saved'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save the pickup location'),
  });

  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap">
      <span className="text-xs text-slate-500 whitespace-nowrap">Default pickup location</span>
      <select
        value={channel.defaultWarehouseId ?? ''}
        disabled={isLoading || save.isPending}
        onChange={(e) => save.mutate(e.target.value ? Number(e.target.value) : null)}
        className="border border-slate-300 rounded-lg px-2 py-1 text-xs min-w-[260px] disabled:opacity-60">
        <option value="">— none (orders arrive without one) —</option>
        {(warehouses as any[]).map((w) => (
          <option key={w.id} value={w.id}>{w.name}{w.city ? ` · ${w.city}` : ''}</option>
        ))}
      </select>
      {save.isPending && <Loader2 size={12} className="animate-spin text-slate-400" />}
      <span className="text-[11px] text-slate-400">New orders from this channel are saved with this pickup location automatically.</span>
    </div>
  );
}

/**
 * Courier stamped on new orders from this channel, one per payment mode.
 *
 * Two dropdowns rather than one because a store routinely ships prepaid and COD on different carriers
 * — COD needs a courier that actually remits, and its rates differ — so a single default would be the
 * wrong one on roughly half the orders. Options come from the channel route (not `/merchants/:id/couriers`,
 * which takes a numeric id and so is unusable from the merchant portal's `me` scope) and are already
 * narrowed to the merchant's active B2C couriers, so the list can't offer something the save refuses.
 */
function DefaultCourierPickers({ channel, merchantPath, onSaved }: { channel: Channel; merchantPath: string | number; onSaved: () => void }) {
  const { data: couriers = [], isLoading } = useQuery({
    queryKey: ['channel-couriers', merchantPath],
    queryFn: async () => (await api.get<any[]>(`/merchants/${merchantPath}/channels/courier-options`)).data,
  });

  const save = useMutation({
    mutationFn: async (patch: { prepaidCourierId?: number | null; codCourierId?: number | null }) =>
      (await api.patch(`/merchants/${merchantPath}/channels/${channel.id}/default-couriers`, patch)).data,
    onSuccess: () => { toast.success('Default courier saved'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save the courier'),
  });

  const noneAssigned = !isLoading && couriers.length === 0;

  const Picker = ({ label, value, field }: { label: string; value: number | null | undefined; field: 'prepaidCourierId' | 'codCourierId' }) => (
    <label className="flex items-center gap-1.5">
      <span className="text-xs text-slate-500 whitespace-nowrap">{label}</span>
      <select
        value={value ?? ''}
        disabled={isLoading || save.isPending || noneAssigned}
        onChange={(e) => save.mutate({ [field]: e.target.value ? Number(e.target.value) : null })}
        className="border border-slate-300 rounded-lg px-2 py-1 text-xs min-w-[190px] disabled:opacity-60">
        <option value="">— none (assign by hand) —</option>
        {couriers.map((c: any) => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="mt-2 flex items-center gap-3 flex-wrap">
      <span className="text-xs text-slate-500 whitespace-nowrap">Default courier</span>
      <Picker label="Prepaid →" value={channel.prepaidCourierId} field="prepaidCourierId" />
      <Picker label="COD →" value={channel.codCourierId} field="codCourierId" />
      {save.isPending && <Loader2 size={12} className="animate-spin text-slate-400" />}
      <span className="text-[11px] text-slate-400">
        {noneAssigned
          ? 'No B2C couriers assigned to this merchant yet — assign one under Merchant → Couriers first.'
          : 'A new order is stamped with the courier matching how it was paid, so it is ready to book.'}
      </span>
    </div>
  );
}

/**
 * "Remove +91 from mobile numbers" rule for one channel.
 *
 * Shopify stores phones in E.164 ("+91 98765 43210") but couriers only accept a bare 10-digit
 * mobile, so a number with the country code still on it books badly or is rejected outright. The rule
 * is ON by default; merchants who deliberately keep the country code can switch it off per channel.
 */
function PhoneRuleToggle({ channel, merchantPath, onSaved }: { channel: Channel; merchantPath: string | number; onSaved: () => void }) {
  // Treat "not set" as ON — that is the server-side default, so the switch matches what actually happens.
  const enabled = channel.stripCountryCode !== false;

  const save = useMutation({
    mutationFn: async (next: boolean) =>
      (await api.patch(`/merchants/${merchantPath}/channels/${channel.id}/phone-rule`, { stripCountryCode: next })).data,
    onSuccess: (_d, next) => { toast.success(next ? 'Country code will be removed from new orders' : 'Numbers will be saved exactly as Shopify sends them'); onSaved(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not save the phone rule'),
  });

  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap">
      <span className="text-xs text-slate-500 whitespace-nowrap">Mobile number rule</span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={save.isPending}
        onClick={() => save.mutate(!enabled)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition disabled:opacity-60 ${enabled ? 'bg-emerald-600' : 'bg-slate-300'}`}>
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${enabled ? 'translate-x-[19px]' : 'translate-x-[3px]'}`} />
      </button>
      <span className={`text-xs font-medium ${enabled ? 'text-emerald-700' : 'text-slate-500'}`}>
        {enabled ? 'Remove +91 automatically' : 'Off — keep the number as-is'}
      </span>
      {save.isPending && <Loader2 size={12} className="animate-spin text-slate-400" />}
      <span className="text-[11px] text-slate-400">
        {enabled
          ? 'e.g. "+91 98765 43210" is saved as "9876543210" — the form couriers accept.'
          : 'Numbers are saved exactly as Shopify sends them, country code included.'}
      </span>
    </div>
  );
}
